import React, { useEffect, useMemo, forwardRef } from 'react';
import * as PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Input, Form, Select, Row, Spin,
} from 'antd';
import ReactCountryFlag from 'react-country-flag';
import { useIntl } from 'react-intl';
import './ViaFields.css';
import { selectAppConfig } from '../../state/domain/appConfig/appConfigReducer';
import { requestCountryList } from '../../state/ui/configuration/company/companyUiActions';

const { Option } = Select;

const AddressInput = forwardRef(({
  value, onChange, countries, getCountries, allowClear, validationStatus,
}, ref) => {
  const triggerChange = changedData => {
    onChange({ ...value, ...changedData });
  };
  useEffect(() => {
    if (!countries) {
      getCountries();
    }
  }, [countries, getCountries]);

  const intl = useIntl();
  // Way too slow to re-render the select every time. Yay for useMemo!
  const options = useMemo(() => (
    countries ? countries.map(country => (
      <Option key={country.id} value={country.id}>
        <Row type="flex" align="middle">
          <ReactCountryFlag code={country.id.toLowerCase()} svg />
          &nbsp;
          {country.description}
        </Row>
      </Option>
    )) : <Option key="spin" value="spin"><Spin /></Option>),
  [countries]);
  return (
    <div>
      <Form.Item style={{ marginBottom: '0px' }} ref={ref} validateStatus={validationStatus && validationStatus.line1 ? 'error' : 'success'} help={validationStatus && validationStatus.line1}>
        <Input
          type="text"
          maxLength={50}
          placeholder={intl.formatMessage({ id: 'addLine1' })}
          value={value.line1}
          allowClear={allowClear}
          onChange={(e) => {
            triggerChange({ line1: e.target.value });
          }}
        />
      </Form.Item>
      <Form.Item style={{ marginBottom: '0px' }} validateStatus={validationStatus && validationStatus.line2 ? 'error' : 'success'} help={validationStatus && validationStatus.line2}>
        <Input
          type="text"
          maxLength={50}
          placeholder={intl.formatMessage({ id: 'addLine2' })}
          value={value.line2}
          allowClear={allowClear}
          onChange={(e) => {
            triggerChange({ line2: e.target.value });
          }}
        />
      </Form.Item>
      <Form.Item style={{ marginBottom: '0px' }} validateStatus={validationStatus && validationStatus.line3 ? 'error' : 'success'} help={validationStatus && validationStatus.line3}>
        <Input
          type="text"
          maxLength={50}
          placeholder={intl.formatMessage({ id: 'addLine3' })}
          value={value.line3}
          allowClear={allowClear}
          onChange={(e) => {
            triggerChange({ line3: e.target.value });
          }}
        />
      </Form.Item>
      <Form.Item style={{ marginBottom: '0px' }} validateStatus={validationStatus && validationStatus.line4 ? 'error' : 'success'} help={validationStatus && validationStatus.line4}>
        <Input
          type="text"
          maxLength={50}
          placeholder={intl.formatMessage({ id: 'addLine4' })}
          value={value.line4}
          allowClear={allowClear}
          onChange={(e) => {
            triggerChange({ line4: e.target.value });
          }}
        />
      </Form.Item>
      <Form.Item style={{ marginBottom: '0px' }} validateStatus={validationStatus && validationStatus.region ? 'error' : 'success'} help={validationStatus && validationStatus.region}>
        <Input
          type="text"
          maxLength={50}
          placeholder={intl.formatMessage({ id: 'region' })}
          value={value.region}
          allowClear={allowClear}
          onChange={(e) => {
            triggerChange({ region: e.target.value });
          }}
        />
      </Form.Item>
      <Form.Item style={{ marginBottom: '0px' }} validateStatus={validationStatus && validationStatus.zipPostcode ? 'error' : 'success'} help={validationStatus && validationStatus.zipPostcode}>
        <Input
          type="text"
          maxLength={20}
          allowClear={allowClear}
          placeholder={intl.formatMessage({ id: 'postcode' })}
          value={value.zipPostcode}
          onChange={(e) => {
            triggerChange({ zipPostcode: e.target.value });
          }}
        />
      </Form.Item>
      <Form.Item style={{ marginBottom: '0px' }} ref={ref} validateStatus={validationStatus && validationStatus.country ? 'error' : 'success'} help={validationStatus && validationStatus.country}>
        <Select
          showSearch
          size="default"
          value={value.country}
          allowClear={allowClear}
          onChange={(newValue) => {
            triggerChange({
              country: newValue,
              countryDescription: newValue && countries.find(country => country.id === newValue).description,
            });
          }}
          placeholder={intl.formatMessage({ id: 'country' })}
          filterOption={(input, option) => countries.find(
            country => country.id === option.props.value,
          ).description.toLowerCase().indexOf(input.toLowerCase()) >= 0}
        >
          {options}
        </Select>
      </Form.Item>
    </div>
  );
});


AddressInput.propTypes = {
  value: PropTypes.shape({
    line1: PropTypes.string,
    line2: PropTypes.string,
    line3: PropTypes.string,
    line4: PropTypes.string,
    region: PropTypes.string,
    zipPostcode: PropTypes.string,
    country: PropTypes.string,
  }),
  getCountries: PropTypes.func.isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape({
    description: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
  })),
  onChange: PropTypes.func.isRequired,
  allowClear: PropTypes.bool,
  validationStatus: PropTypes.shape({
    line1: PropTypes.string,
    line2: PropTypes.string,
    line3: PropTypes.string,
    line4: PropTypes.string,
    region: PropTypes.string,
    zipPostcode: PropTypes.string,
    country: PropTypes.string,
  }),
};

AddressInput.defaultProps = {
  value: {},
  countries: null,
  allowClear: false,
  validationStatus: {},
};

/**
 * A function for connecting redux state to the component props.
 */
const mapStateToProps = state => ({
  countries: selectAppConfig(state, 'countries') ? selectAppConfig(state, 'countries').countries : null,
});

/**
 * A function for connecting redux action to the component props.
 */
const mapDispatchToProps = dispatch => ({
  getCountries: () => dispatch(requestCountryList()),
});

export default connect(mapStateToProps, mapDispatchToProps)(AddressInput);
